// HMGContext.js
import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import Http from "./services/Http";
import { useLocation } from "react-router-dom";
import SockJS from "sockjs-client";
import { Client } from "@stomp/stompjs";
import { TruePlanVars } from "./TruePlan";
import { handleApiError } from "./Utils/errorHandler";
import dataService from "./services/data.service";

// Create a context for managing selected HMG state
const HMGContext = createContext();

export const HMGProvider = ({ children }) => {
  const userRef = React.useRef(null);
  const [selectedHMG, setSelectedHMG] = useState(null);
  const [actionStatePayload, setActionStatePayload] = useState({});
  const [handleResponse, setHandleResponse] = useState({});
  const [planId, setPlanId] = useState("");
  const [showHeaderBtn, setShowHeaderBtn] = useState(false);
  const [refreshEditedCount, setRefreshEditedCount] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [showCoaHeaderBtn, setShowCoaHeaderBtn] = useState(false);
  const [headerActions, setHeaderActions] = useState()
  const [viewAsUserOpen, setViewAsUserOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null);
  const [viewAsLoading, setViewAsLoading] = useState(false);
  const [compareHighLightLoading, setCompareHighLightLoading] = useState(false);
  const [planDetailsRollUpLoader, setPlanDetailsRollUpLoader] = useState(false);
  const [stopLoading , setStopLoading] = useState(false);
  const [lasteditedcardLoading, setLasteditedcardLoading] = useState(true);
  const [structure, setStructure] = useState("gl-structure");
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [currentModalName, setCurrentModalName] = useState(null);
  const [users, setUsers] = useState([]);
  const [showPlanTableVarience , setShowPlanTableVarience] = useState(false)
  const [isNestedApiLoading, setIsNestedApiLoading] = useState(false);
  const [planTableRefreshTrigger, setPlanTableRefreshTrigger] = useState(null);
  const location = useLocation();
  // Handle trailing slashes - remove them before extracting ID
  const pathnameWithoutTrailingSlash = location.pathname.replace(/\/$/, '');
  const id = pathnameWithoutTrailingSlash.split("/").pop();
  const isPlanVersion = location.pathname.includes("/planVersion");
  const isCritiqueDetails = location.pathname.includes("/critique/details/");
  const [selectedCurrency, setSelectedCurrency] = useState("USD");
  const [currencyRates, setCurrencyRates] = useState({
    USD: 1,
    INR: 83.21,
    GBP: 0.73,
    JPY: 147.21
  });

  const [isPlanDetailsDenseView, setIsPlanDetailsDenseView] = useState(false);
  // Plan details: show all 12 months (true) vs single swipeable month (false).
  // Shared across both the Nested and Line Item tables. Defaults to all-months view.
  const [showAllMonths, setShowAllMonths] = useState(true);
  // Plan Highlights month selection (YYYYMM, null = none). Shared with the Nested and
  // Line-Items tables so a highlights month pre-positions both grids' single-month view.
  const [selectedYearMonth, setSelectedYearMonth] = useState(null);
  const [isAllPlansFilterDenseView, setIsAllPlansFilterDenseView] = useState(false);
    const [otelierHasChangedItems, setOtelierHasChangedItems] = useState(false);
  const [headerComparisonPlanLabel, setHeaderComparisonPlanLabel] = useState(null);
  // TPX-1903 multi-comparison: canonical configured comparisons (see planComparisonModel.js) plus
  // header labels and the Filters "Comparisons" selection. selectedComparisonIds holds the set of
  // selected comparison keys (planComparisonName); it scopes ALL comparison surfaces (highlights,
  // roll-ups, grid, edit value). null = "all selected" (default until the user deselects any chip).
  const [planComparisons, setPlanComparisons] = useState([]);
  const [headerComparisonLabels, setHeaderComparisonLabels] = useState([]);
  const [selectedComparisonIds, setSelectedComparisonIds] = useState(null);
  // TPX-1457 "calculate from last year": while on, the Edit Value dialogs show each comparison's Rate row
  // as the rate the planner entered and its Total row recalculated from that rate against the comparison's
  // own stat, with variance chips hidden. Display-only and deliberately ephemeral — it resets on reload so
  // nobody is left reading restated figures without having turned them on.
  const [calculateFromLastYear, setCalculateFromLastYear] = useState(false);
  // Live trigger for the global HMGReprocessingLoader so it can appear without a page refresh
  // (the server-side isReprocessing flag is only read at page load).
  const [isReprocessingActive, setIsReprocessingActive] = useState(false);
  // Plan-scoped reprocess lock ({ planId, locked, failed, status }) while a save-as-new-version COA alignment
  // runs on the open plan. Owned by the plan details page (set on enter, cleared on leave); read by
  // PlanReprocessingLoader. Unlike isReprocessingActive it never locks the rest of the HMG.
  const [planReprocessStatus, setPlanReprocessStatus] = useState(null);

  const {
    handleClose,
    setLoading,
    setEditedCells,
    setIsEdited,
    setOpenSuccessSnack,
    setPlanDetailsUpdateload,
    setRefreshPageData,
    setOpenErrorSnack,
    setOpenVersionModal,
  } = handleResponse;

  const handleSaveAction = async () => {
    try{
    setIsEdited(false);
    setLoading(true);
    setShowHeaderBtn(false);
    setPlanDetailsUpdateload(true)
    setPlanDetailsRollUpLoader(true)
    setStopLoading(false)
    setIsNestedApiLoading(true)
    const resp = await Http.POST("plan-details/update", actionStatePayload);
    setLoading(false);
    if (resp && resp.status && resp.status == 200) {
      setEditedCells(new Set()); // Reset edited cells on successful save
      setOpenSuccessSnack(true);
      setIsNestedApiLoading(false)
      setPlanDetailsUpdateload(new Date());
      setRefreshPageData(new Date());
      setPlanTableRefreshTrigger(new Date());
      
      // Prefetch dialog data in background after successful save (non-blocking)
      const planIdForPrefetch = actionStatePayload?.planId || location.pathname.split("/").pop();
      const organizationId = actionStatePayload?.organizationId || dataService.getSelectedHMGId();
      if (planIdForPrefetch && organizationId) {
        // After a successful plan save, clear the "show only unsaved" filter in persisted preferences
        // (user preference merge keeps other PLAN_DETAILS_LINE_ITEMS_VIEW fields)
        Http.POST("/user-preferences/save", {
          organizationId,
          planId: planIdForPrefetch,
          preferenceType: "PLAN_DETAILS_LINE_ITEMS_VIEW",
          preferenceData: { showOnlyUnSavedData: false },
        }).catch((err) => {
          console.warn("Failed to clear showOnlyUnSavedData preference after plan save", err);
        });
        try {
          window.dispatchEvent(
            new CustomEvent("plan-details-final-saved", { detail: { planId: planIdForPrefetch } })
          );
        } catch (e) {
          // non-browser
        }
        // NOTE: dialog prefetch is intentionally NOT re-run here. A routine plan-value save
        // does not change the prefetched reference data (account types, structure/functional
        // codes, department mappings), so re-running the ~8-call batch on every save was wasted
        // load. The prefetch cache is invalidated by the flows that DO change reference data
        // (department-mapping save event + COA mapping publish) and refetched lazily on next entry.
      }
    } else {
      setPlanDetailsUpdateload(false);
      setIsNestedApiLoading(false);
      setPlanDetailsRollUpLoader(false);
      setIsEdited(true);
      setOpenErrorSnack(true);
    }
    handleClose();
    return resp;
  } catch (error) {
    setPlanDetailsUpdateload(false);
    setIsNestedApiLoading(false);
    setPlanDetailsRollUpLoader(false);
    console.error("Error in handleSaveAction:", error);
  }
  };

  const handleSaveNewAction = async () => {
    // setOpenVersionModal(true);
  };

  const handleDiscard = async () => {
    try {
      setLoading(true);
      handleClose();
      setShowHeaderBtn(false);
      setStopLoading(false);
      setIsNestedApiLoading(true);
      setPlanDetailsRollUpLoader(true); // Set rollup loader to show loading state
      const resp = await Http.DELETE("plan-draft/discard/" + planId);
      setLoading(false);
      if (resp && resp.status && resp.status === 200) {
        setEditedCells(new Set()); // Reset edited cells on successful save
        setIsEdited(false);
        setIsNestedApiLoading(false);
        setOpenSuccessSnack(true);
        setPlanDetailsUpdateload(new Date());
        setRefreshPageData(new Date());
        setPlanTableRefreshTrigger(new Date()); // This will trigger rollup update and reset loader

        // After successful discard, clear the "show only unsaved" filter so the
        // toggle in PlanDetailsView resets in sync with isEdited going false.
        const organizationId = dataService.getSelectedHMGId();
        if (planId && organizationId) {
          Http.POST("/user-preferences/save", {
            organizationId,
            planId,
            preferenceType: "PLAN_DETAILS_LINE_ITEMS_VIEW",
            preferenceData: { showOnlyUnSavedData: false },
          }).catch((err) => {
            console.warn("Failed to clear showOnlyUnSavedData preference after discard", err);
          });
          try {
            window.dispatchEvent(
              new CustomEvent("plan-details-final-saved", { detail: { planId } })
            );
          } catch (e) {
            // non-browser
          }
        }
      } else {
        setIsEdited(false);
        setIsNestedApiLoading(false);
        setPlanDetailsRollUpLoader(false); // Reset loader on error
        setOpenErrorSnack(true);
      }
      setLoading(false);
      return resp;
    } catch (error) {
      setLoading(false);
      setIsEdited(false);
      setIsNestedApiLoading(false);
      setPlanDetailsRollUpLoader(false); // Reset loader on error
      setOpenErrorSnack(true);
      console.error("Error in handleDiscard:", error);
      // Use global error handler for consistent logging (no toast in context)
      handleApiError(error, () => {}, "", { showToast: false });
    }
  };


  async function handleViewAsUserOpen(selectedUser) {
    try {
    setViewAsLoading(true);
      let payload = { "email" : selectedUser.id };
      if (selectedUser?.id) {
        sessionStorage.setItem("assumedUserEmail", String(selectedUser.id));
      }

      const resp = await Http.POST("user/assumeUser", payload);
      setLoading(false);
      if (resp && resp.status && resp.status == 200) {
        setViewAsUserOpen(resp.data.hasAssumedUser)
        sessionStorage.setItem('showViewAsUser', resp.data.hasAssumedUser);
        sessionStorage.setItem('selectedUser', resp.data.assumedUserFullName);
        if (resp.data?.id != null) {
          sessionStorage.setItem('assumedUserId', String(resp.data.id));
        }
        if (resp.data?.email) {
          sessionStorage.setItem('assumedUserEmail', String(resp.data.email));
        }
        setSelectedUser(resp.data.assumedUserFullName);
        setViewAsLoading(false);
        window.location.reload();
      } else {

      }
    } catch (error) {
      setLoading(false);
      setViewAsLoading(false);
      console.error("Error in handleViewAsUserOpen:", error);
      // Use global error handler for consistent logging (no toast in context)
      handleApiError(error, () => {}, "", { showToast: false });
    }
  }

  async function handleViewAsUserClose() {
    try {
      const resp = await Http.GET("user/releaseUser");
      setLoading(false);
      if (resp && resp.status && resp.status == 200) {
        setViewAsUserOpen(false)
        sessionStorage.removeItem('showViewAsUser');
        sessionStorage.removeItem('selectedUser');
        sessionStorage.removeItem('assumedUserId');
        sessionStorage.removeItem('assumedUserEmail');
        window.location.reload();
      } else {

      }
    } catch (error) {
      setLoading(false);
      console.error("Error in handleViewAsUserClose:", error);
      // Use global error handler for consistent logging (no toast in context)
      handleApiError(error, () => {}, "", { showToast: false });
    }
  }

  useEffect(() => {
    let showViewAsUser = sessionStorage.getItem('showViewAsUser')
    let selectedUser = sessionStorage.getItem('selectedUser')
    if (showViewAsUser && selectedUser) {
      setViewAsUserOpen(true)
      // console.log(JSON.parse(selectedUser) ,"selectedUser");
      // setSelectedUser(JSON.parse(selectedUser || JSON.stringify(" ")))
      setSelectedUser(selectedUser)
    } else {  
      setViewAsUserOpen(false)
    }
  }, [])

  useEffect(() => {
    // Load currency from sessionStorage on component mount
    const savedCurrency = sessionStorage.getItem('selectedCurrency');
    if (savedCurrency) {
      setSelectedCurrency(savedCurrency);
    } else {
      setSelectedCurrency("USD");
      sessionStorage.setItem('selectedCurrency', "USD");
    }
  }, []);

  // WebSocket client refs
  const stompClientRef = useRef(null);
  const subscriptionRef = useRef(null);

  // Convert WebSocket user data to array format (for backward compatibility)
  const convertUsersToArray = (usersMap) => {
    if (!usersMap || typeof usersMap !== 'object') {
      return [];
    }
    
    // Convert Map/object to array of users with IDs
    return Object.entries(usersMap).map(([key, user]) => ({
      ...user,
      id: key.replace(/\s+/g, "-").toLowerCase(),
      // Map backend fields to frontend expected fields
      // active: user.active || false,
      fullName: user.fullName || key,
    }));
  };

  // Subscribe to plan presence via WebSocket
  const subscribeToPlanPresence = async (planId) => {
    if (!planId || !isPlanVersion) {
      return;
    }

    try {
      // Get JWT token
      // const token = await Http.getAccessToken();
      const token = await Http.getIdToken();
      if (!token) {
        console.error("Failed to get access token for WebSocket connection");
        return;
      }

       const socketUrl = TruePlanVars.varSocketURL || "http://localhost:8080/ws";

      // Get socket URL
      // const socketUrl = TruePlanVars.varSocketURL || `${TruePlanVars.varServiceURL}/ws`;
      
      // Create WebSocket connection
      const socket = new SockJS(socketUrl);
      const client = new Client({
        webSocketFactory: () => socket,
        // Base + jitter so presence clients don't reconnect in lockstep after a backend blip.
        reconnectDelay: 5000 + Math.floor(Math.random() * 5000),
        heartbeatIncoming: 4000,
        heartbeatOutgoing: 4000,
        connectHeaders: {
          'Authorization': `Bearer ${token}`
        },
        onConnect: () => {
          console.log('WebSocket connected for plan presence:', planId);
          
          // Subscribe to plan presence topic
          subscriptionRef.current = client.subscribe(
            `/topic/plan-presence/${planId}`,
            (message) => {
              try {
                
                const activeUsersMap = JSON.parse(message.body);
                // Convert to array format for backward compatibility
                const usersArray = convertUsersToArray(activeUsersMap);
                
                const oldUsers = userRef.current || [];
                
                // Compare: either user count changed OR field changed
                const hasChanged =
                  oldUsers.length !== usersArray.length ||
                  usersArray.some(newUser => {
                    const oldUser = oldUsers.find(u => u.id === newUser.id);
                    return !oldUser || oldUser.active !== newUser.active;
                  });

                if (hasChanged) {
                  userRef.current = usersArray;
                  setUsers(usersArray);
                }
              } catch (error) {
                console.error("Error parsing plan presence message:", error);
              }
            }
          );
        },
        onDisconnect: () => {
          console.log('WebSocket disconnected for plan presence');
        },
        onStompError: (frame) => {
          console.error('STOMP error:', frame);
        },
        onWebSocketError: (event) => {
          console.error('WebSocket error:', event);
        },
        debug: () => {},
      });

      stompClientRef.current = client;
      client.activate();
    } catch (error) {
      console.error("Error setting up WebSocket connection:", error);
    }
  };

  // Unsubscribe from plan presence
  const unsubscribeFromPlanPresence = (planId) => {
    // Explicitly unsubscribe (optional, will happen on disconnect anyway)
    if (stompClientRef.current && stompClientRef.current.connected && planId) {
      try {
        stompClientRef.current.publish({
          destination: `/tpx/plan-presence/${planId}/unsubscribe`,
          body: JSON.stringify({})
        });
      } catch (error) {
        console.error("Error unsubscribing from plan presence:", error);
      }
    }
    
    // Unsubscribe from topic
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
      subscriptionRef.current = null;
    }
    
    // Disconnect WebSocket
    if (stompClientRef.current) {
      try {
        stompClientRef.current.deactivate();
      } catch (error) {
        console.error("Error deactivating WebSocket:", error);
      }
      stompClientRef.current = null;
    }
  };

  // Subscribe to critique presence via WebSocket (same pattern as plan presence)
  const subscribeToCritiquePresence = async (critiqueId) => {
    if (!critiqueId || !isCritiqueDetails) return;
    try {
      const token = await Http.getIdToken();
      if (!token) return;
      const socketUrl = TruePlanVars.varSocketURL || "http://localhost:8080/ws";
      const socket = new SockJS(socketUrl);
      const client = new Client({
        webSocketFactory: () => socket,
        // Base + jitter so presence clients don't reconnect in lockstep after a backend blip.
        reconnectDelay: 5000 + Math.floor(Math.random() * 5000),
        heartbeatIncoming: 4000,
        heartbeatOutgoing: 4000,
        connectHeaders: { 'Authorization': `Bearer ${token}` },
        onConnect: () => {
          subscriptionRef.current = client.subscribe(
            `/topic/critique-presence/${critiqueId}`,
            (message) => {
              try {
                const activeUsersMap = JSON.parse(message.body);
                const usersArray = convertUsersToArray(activeUsersMap);
                const oldUsers = userRef.current || [];
                const hasChanged = oldUsers.length !== usersArray.length || usersArray.some(newUser => {
                  const oldUser = oldUsers.find(u => u.id === newUser.id);
                  return !oldUser || oldUser.active !== newUser.active;
                });
                if (hasChanged) {
                  userRef.current = usersArray;
                  setUsers(usersArray);
                }
              } catch (err) {
                console.error("Error parsing critique presence message:", err);
              }
            }
          );
        },
        onDisconnect: () => {},
        onStompError: (frame) => { console.error("STOMP error:", frame); },
        onWebSocketError: (event) => { console.error("WebSocket error:", event); },
        debug: () => {},
      });
      stompClientRef.current = client;
      client.activate();
    } catch (error) {
      console.error("Error setting up critique presence WebSocket:", error);
    }
  };

  const unsubscribeFromCritiquePresence = (critiqueId) => {
    if (stompClientRef.current?.connected && critiqueId) {
      try {
        stompClientRef.current.publish({
          destination: `/tpx/critique-presence/${critiqueId}/unsubscribe`,
          body: JSON.stringify({}),
        });
      } catch (e) {
        console.error("Error unsubscribing from critique presence:", e);
      }
    }
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
      subscriptionRef.current = null;
    }
    if (stompClientRef.current) {
      try {
        stompClientRef.current.deactivate();
      } catch (e) {
        console.error("Error deactivating critique WebSocket:", e);
      }
      stompClientRef.current = null;
    }
  };

  // Legacy fetchUsers function (plan or critique presence based on route)
  const fetchUsers = async () => {
    try {
      const endpoint = isCritiqueDetails ? `critique-presence/${id}/users` : `plan-presence/${id}/users`;
      const response = await Http.GET(endpoint);
      const newUsersArray = Object.entries(response.data || {}).map(([key, user]) => ({
        ...user,
        id: key.replace(/\s+/g, "-").toLowerCase(),
        fullName: user.fullName || key,
      }));

      const oldUsers = userRef.current || [];
      const hasChanged =
        oldUsers.length !== newUsersArray.length ||
        newUsersArray.some(newUser => {
          const oldUser = oldUsers.find(u => u.id === newUser.id);
          return !oldUser || oldUser.active !== newUser.active;
        });
      if (hasChanged) {
        userRef.current = newUsersArray;
        setUsers(newUsersArray);
      }
    } catch (error) {
      if (isCritiqueDetails) {
        userRef.current = [];
        setUsers([]);
      }
      console.error(error);
    }
  };

  // WebSocket subscription effect - plan or critique presence
  useEffect(() => {
    if (id && isPlanVersion) {
      subscribeToPlanPresence(id);
      return () => { unsubscribeFromPlanPresence(id); };
    }
    if (id && isCritiqueDetails) {
      subscribeToCritiquePresence(id);
      return () => { unsubscribeFromCritiquePresence(id); };
    }
    if (stompClientRef.current) {
      unsubscribeFromPlanPresence(id);
      unsubscribeFromCritiquePresence(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isPlanVersion, isCritiqueDetails]);

  // Handle browser/tab close - unsubscribe
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (id && (isPlanVersion || isCritiqueDetails) && stompClientRef.current) {
        if (isPlanVersion) unsubscribeFromPlanPresence(id);
        else unsubscribeFromCritiquePresence(id);
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [id, isPlanVersion, isCritiqueDetails]);

  return (
    <HMGContext.Provider
      value={{
        selectedHMG,
        setSelectedHMG,
        actionStatePayload,
        setActionStatePayload,
        handleResponse,
        handleSaveNewAction,
        handleSaveAction,
        setHandleResponse,
        handleDiscard,
        setPlanId,
        showHeaderBtn,
        setShowHeaderBtn,
        refreshEditedCount,
        setRefreshEditedCount,
        isSidebarOpen,
        setIsSidebarOpen,
        showCoaHeaderBtn,
        setShowCoaHeaderBtn,
        setHeaderActions,
        headerActions,
        viewAsUserOpen,
        handleViewAsUserOpen,
        handleViewAsUserClose,
        selectedUser,
        viewAsLoading,
        setViewAsLoading,
        compareHighLightLoading,
        setCompareHighLightLoading,
        planDetailsRollUpLoader, 
        setPlanDetailsRollUpLoader ,
        stopLoading, 
        setStopLoading,
        structure, 
        setStructure,
        lasteditedcardLoading, 
        setLasteditedcardLoading,
        selectedCurrency,
        setSelectedCurrency,
        currencyRates,
        setCurrencyRates,
        isReadOnly, 
        setIsReadOnly,
        isPlanDetailsDenseView,
        setIsPlanDetailsDenseView,
        showAllMonths,
        setShowAllMonths,
        selectedYearMonth,
        setSelectedYearMonth,
        isAllPlansFilterDenseView,
        setIsAllPlansFilterDenseView,
        users, 
        setUsers,
        fetchUsers,
        currentModalName, 
        setCurrentModalName,
        showPlanTableVarience , setShowPlanTableVarience,
        isNestedApiLoading, setIsNestedApiLoading,
        planTableRefreshTrigger ,
        setPlanTableRefreshTrigger,
        otelierHasChangedItems,
        setOtelierHasChangedItems,
        headerComparisonPlanLabel,
        setHeaderComparisonPlanLabel,
        planComparisons,
        setPlanComparisons,
        headerComparisonLabels,
        setHeaderComparisonLabels,
        selectedComparisonIds,
        setSelectedComparisonIds,
        calculateFromLastYear,
        setCalculateFromLastYear,
        isReprocessingActive,
        setIsReprocessingActive,
        planReprocessStatus,
        setPlanReprocessStatus
      }}
    >
      {children}
    </HMGContext.Provider>
  );
};

// Custom hook to access the context
export const useHMG = () => useContext(HMGContext);
